import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ShoppingCart, Plus, Minus, Search, Trash2, Eye, Save, ArrowLeft, Upload, Download,
  Truck, Package, Users, Receipt, X, FileText, CreditCard, Barcode, Calendar, ChevronRight,
  Printer, Tag, TrendingUp, Activity,
} from "lucide-react";
import { api, API, getToken } from "../../../lib/auth";
import { fmtMoney as fmt } from "../../../lib/currency";
import { Card, PageHeader, PrimaryButton, Field, TextInput, Select, SectionHeader } from "../products/_shared";

// ═══════════════════════════════ ALL SALES ═══════════════════════════════
export function AllSales() {
  const { subdomain } = useParams();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [payment, setPayment] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const params = {};
    if (search) params.q = search;
    if (status !== "all") params.status = status;
    if (payment !== "all") params.payment_status = payment;
    const r = await api.get("/t/sales", { params });
    setRows(r.data);
  };
  useEffect(() => { load(); }, [status, payment]); // eslint-disable-line

  const remove = async (s) => {
    if (!window.confirm(`Delete sale ${s.invoice_number}?`)) return;
    await api.delete(`/t/sales/${s.id}`);
    toast.success("Deleted");
    load();
  };

  const total = rows.reduce((s, r) => s + (r.total || 0), 0);
  const paid = rows.reduce((s, r) => s + (r.paid || 0), 0);
  const due = rows.reduce((s, r) => s + (r.due || 0), 0);

  return (
    <div data-testid="sales-page" className="p-6 lg:p-8">
      <PageHeader title="All Sales" breadcrumb="Sales · All Sales"
        actions={<Link to={`/t/${subdomain}/sales/new`}><PrimaryButton icon={Plus} testid="sales-new">Create Sale</PrimaryButton></Link>} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[
          { lbl: "Invoices", v: rows.length, bg: "bg-[#E8F5E9]", fg: "text-[#1FA868]", icon: Receipt },
          { lbl: "Revenue", v: fmt(total), bg: "bg-violet-50", fg: "text-violet-600", icon: TrendingUp },
          { lbl: "Paid", v: fmt(paid), bg: "bg-sky-50", fg: "text-sky-600", icon: CreditCard },
          { lbl: "Outstanding", v: fmt(due), bg: "bg-amber-50", fg: "text-amber-700", icon: FileText },
        ].map((s) => { const I = s.icon; return (
          <Card key={s.lbl} className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl ${s.bg} ${s.fg} flex items-center justify-center`}><I size={16} /></div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-black/55 font-semibold">{s.lbl}</div>
                <div className="font-display font-extrabold text-base text-black truncate">{s.v}</div>
              </div>
            </div>
          </Card>
        ); })}
      </div>

      <Card className="p-3 flex flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2 bg-[#FAFBFB] rounded-lg px-3 py-2 flex-1 min-w-[260px]">
          <Search size={16} className="text-black/40" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by invoice # or customer…" data-testid="sales-search"
            className="flex-1 bg-transparent focus:outline-none text-sm" />
          <button onClick={load} className="text-xs font-bold text-[#1FA868]">Search</button>
        </div>
        {["all", "completed", "pending", "cancelled"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`sales-status-${s}`}
            className={`px-3 py-2 rounded-lg text-xs font-semibold capitalize ${status === s ? "bg-[#1FA868] text-white" : "bg-white border border-black/10"}`}>{s}</button>
        ))}
        <select value={payment} onChange={(e) => setPayment(e.target.value)} data-testid="sales-payment-filter"
          className="px-3 py-2 rounded-lg border border-black/15 bg-white text-sm">
          <option value="all">All payments</option>
          <option value="paid">Paid</option>
          <option value="partial">Partial</option>
          <option value="unpaid">Unpaid</option>
        </select>
      </Card>

      <Card>

  {/* 💻 DESKTOP TABLE */}
  <div className="hidden md:block overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase tracking-wider">
        <tr>
          <th className="text-left p-3.5 font-bold">Customer</th>
          <th className="text-left p-3.5 font-bold">Invoice</th>
          <th className="text-left p-3.5 font-bold">Total</th>
          <th className="text-left p-3.5 font-bold">Paid</th>
          <th className="text-left p-3.5 font-bold">Due</th>
          <th className="text-left p-3.5 font-bold">Status</th>
          <th className="text-right p-3.5 font-bold">Actions</th>
        </tr>
      </thead>

      <tbody className="divide-y divide-black/5">
        {(rows || []).map((s) => (
          <tr key={s.id} className="hover:bg-[#FAFBFB]">

            <td className="p-3.5 font-semibold">{s.customer_name}</td>

            <td className="p-3.5 text-xs">{s.invoice_number}</td>

            <td className="p-3.5 font-bold">{fmt(s.total)}</td>

            <td className="p-3.5 text-[#1FA868]">{fmt(s.paid)}</td>

            <td className="p-3.5 text-rose-600">{fmt(s.due)}</td>

            <td className="p-3.5">
              <span className={`text-xs px-2 py-1 rounded ${
                s.payment_status === "paid"
                  ? "bg-[#E8F5E9] text-[#1FA868]"
                  : s.payment_status === "partial"
                  ? "bg-amber-50 text-amber-700"
                  : "bg-rose-50 text-rose-600"
              }`}>
                {s.payment_status}
              </span>
            </td>

            <td className="p-3.5 text-right">
              <div className="flex justify-end gap-2">
                <button onClick={() => setSelected(s)}>
                  <Eye size={14} />
                </button>
                <button onClick={() => remove(s)}>
                  <Trash2 size={14} />
                </button>
              </div>
            </td>

          </tr>
        ))}

        {(rows || []).length === 0 && (
          <tr>
            <td colSpan="7" className="p-8 text-center text-black/50">
              No data available
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>


  {/* 📱 MOBILE CARDS */}
  <div className="md:hidden space-y-3 p-3">

    {(rows || []).length === 0 && (
      <div className="text-center text-black/50 py-10">
        No data available
      </div>
    )}

    {(rows || []).map((s) => (
      <div key={s.id} className="bg-white border border-black/5 rounded-xl p-4 shadow-sm">

        {/* Top */}
        <div className="flex justify-between items-start">
          <div>
            <div className="font-semibold text-black">{s.customer_name}</div>
            <div className="text-xs text-black/50">{s.invoice_number}</div>
          </div>

          <span className={`text-xs font-bold px-2 py-1 rounded ${
            s.payment_status === "paid"
              ? "bg-[#E8F5E9] text-[#1FA868]"
              : s.payment_status === "partial"
              ? "bg-amber-50 text-amber-700"
              : "bg-rose-50 text-rose-600"
          }`}>
            {s.payment_status}
          </span>
        </div>

        {/* Info */}
        <div className="grid grid-cols-2 gap-3 mt-3 text-sm">

          <div>
            <div className="text-xs text-black/50">Total</div>
            <div className="font-bold">{fmt(s.total)}</div>
          </div>

          <div>
            <div className="text-xs text-black/50">Paid</div>
            <div className="text-[#1FA868]">{fmt(s.paid)}</div>
          </div>

          <div>
            <div className="text-xs text-black/50">Due</div>
            <div className="text-rose-600">{fmt(s.due)}</div>
          </div>

          <div>
            <div className="text-xs text-black/50">Items</div>
            <div>{(s.items || []).length}</div>
          </div>

        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={() => setSelected(s)}
            className="p-2 rounded bg-[#E8F5E9] text-[#1FA868]"
          >
            <Eye size={14} />
          </button>

          <button
            onClick={() => remove(s)}
            className="p-2 rounded bg-rose-50 text-rose-600"
          >
            <Trash2 size={14} />
          </button>
        </div>

      </div>
    ))}

  </div>

</Card>

      {selected && <ReceiptDrawer sale={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function ReceiptDrawer({ sale, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-lg bg-white h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h3 className="font-display font-bold text-xl">{sale.invoice_number}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-black/5"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex justify-between text-sm"><span className="text-black/55">Customer</span><span className="font-semibold">{sale.customer_name}</span></div>
          <div className="flex justify-between text-sm"><span className="text-black/55">Date</span><span>{sale.date}</span></div>
          <div className="flex justify-between text-sm"><span className="text-black/55">Method</span><span className="capitalize">{sale.payment_method}</span></div>
          <div>
            <div className="text-[10px] uppercase font-bold text-black/55 mb-2">Items</div>
            <div className="divide-y divide-black/8">
              {(sale.items || []).map((i, idx) => (
                <div key={idx} className="flex justify-between py-2 text-sm">
                  <span>{i.name} <span className="text-black/45">× {i.qty}</span></span>
                  <span className="font-bold tabular-nums">{fmt(i.line_total ?? i.qty * i.unit_price)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="pt-3 border-t border-black/10 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-black/55">Subtotal</span><span>{fmt(sale.subtotal)}</span></div>
            {sale.order_discount > 0 && <div className="flex justify-between text-rose-600"><span>Discount</span><span>-{fmt(sale.order_discount)}</span></div>}
            {sale.order_tax > 0 && <div className="flex justify-between"><span className="text-black/55">Tax ({sale.order_tax}%)</span><span>+{fmt(sale.subtotal * sale.order_tax / 100)}</span></div>}
            {sale.shipping > 0 && <div className="flex justify-between"><span className="text-black/55">Shipping</span><span>+{fmt(sale.shipping)}</span></div>}
            <div className="flex justify-between font-bold text-base pt-2 border-t border-black/10"><span>Total</span><span>{fmt(sale.total)}</span></div>
            <div className="flex justify-between text-[#1FA868]"><span>Paid</span><span>{fmt(sale.paid)}</span></div>
            {sale.due > 0 && <div className="flex justify-between text-rose-600 font-bold"><span>Due</span><span>{fmt(sale.due)}</span></div>}
          </div>
          <button onClick={() => window.print()} className="w-full py-2.5 rounded-xl bg-black/5 hover:bg-black/10 text-sm font-semibold inline-flex items-center justify-center gap-2">
            <Printer size={14} /> Print receipt
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════ CREATE SALE ═══════════════════════════════
function NewCustomerMini({ open, onClose, onCreated }) {
  const [name, setName] = useState(""); const [phone, setPhone] = useState(""); const [busy, setBusy] = useState(false);
  if (!open) return null;
  const submit = async () => {
    if (!name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const r = await api.post("/t/customers", { name, phone, active: true });
      toast.success("Customer added"); onCreated(r.data); setName(""); setPhone(""); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-bold text-lg">New Customer</h3>
        <div className="mt-3 space-y-2">
          <TextInput value={name} onChange={setName} placeholder="Name" testid="nc-name" />
          <TextInput value={phone} onChange={setPhone} placeholder="Phone" testid="nc-phone" />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 rounded-lg border border-black/15 text-sm">Cancel</button>
          <PrimaryButton onClick={submit} disabled={busy} testid="nc-save">{busy ? "…" : "Create"}</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

export function CreateSale() {
  const { subdomain } = useParams();
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    customer_id: "", customer_name: "Walk-in Customer",
    warehouse_id: "", sales_agent_id: "",
    items: [], order_tax: 0, order_discount: 0, shipping: 0,
    paid: 0, payment_method: "cash", status: "completed", note: "", channel: "manual",
  });

  useEffect(() => {
    api.get("/t/customers").then((r) => {
      setCustomers(r.data);
      const walkin = r.data.find((c) => c.name.toLowerCase().includes("walk")) || r.data[0];
      if (walkin) setForm((f) => ({ ...f, customer_id: walkin.id, customer_name: walkin.name }));
    });
    api.get("/t/warehouses").then((r) => {
      setWarehouses(r.data);
      if (r.data[0]) setForm((f) => ({ ...f, warehouse_id: r.data[0].id }));
    });
    api.get("/t/products").then((r) => setProducts(r.data));
  }, []);

  useEffect(() => {
    if (!search) return setResults([]);
    const q = search.toLowerCase();
    setResults(products.filter((p) => p.name.toLowerCase().includes(q) || p.code?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q)).slice(0, 6));
  }, [search, products]);

  const addItem = (p) => {
    const exists = form.items.find((i) => i.product_id === p.id);
    if (exists) {
      setForm({ ...form, items: form.items.map((i) => i.product_id === p.id ? { ...i, qty: i.qty + 1 } : i) });
    } else {
      setForm({ ...form, items: [...form.items, { product_id: p.id, name: p.name, qty: 1, unit_price: p.price, discount: 0, tax: 0, stock: p.stock }] });
    }
    setSearch(""); setResults([]);
  };

  const upd = (i, patch) => {
    const items = [...form.items]; items[i] = { ...items[i], ...patch }; setForm({ ...form, items });
  };
  const remove = (i) => setForm({ ...form, items: form.items.filter((_, k) => k !== i) });

  const subtotal = form.items.reduce((s, i) => s + (i.qty * i.unit_price - (i.discount || 0) + (i.tax || 0)), 0);
  const tax = (subtotal * (form.order_tax || 0)) / 100;
  const total = subtotal - (form.order_discount || 0) + (form.shipping || 0) + tax;
  const due = Math.max(0, total - (form.paid || 0));

  const submit = async () => {
    if (!form.warehouse_id) return toast.error("Warehouse required");
    if (form.items.length === 0) return toast.error("Add at least one item");
    setBusy(true);
    try {
      await api.post("/t/sales", form);
      toast.success("Sale created");
      nav(`/t/${subdomain}/sales`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="create-sale-page" className="p-6 lg:p-8 pb-32">
      <Link to={`/t/${subdomain}/sales`} className="inline-flex items-center gap-1.5 text-sm text-black/55 hover:text-black mb-2">
        <ArrowLeft size={13} /> Back
      </Link>
      <h1 className="font-display font-extrabold text-3xl text-black tracking-tight">Create Sale</h1>
      <div className="text-xs text-black/55 mt-1 inline-flex items-center gap-1 mb-6">
        <Link to={`/t/${subdomain}/sales`} className="hover:text-[#1FA868]">All Sales</Link> <ChevronRight size={11} className="text-black/30" /> Create Sale
      </div>

      <Card className="p-6 lg:p-7 mb-5">
        <div className="grid md:grid-cols-3 gap-4">
          <Field lbl="Date" required>
            <TextInput type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="cs-date" />
          </Field>
          <Field lbl="Customer" required>
            <Select value={form.customer_id} onChange={(v) => {
              const c = customers.find((x) => x.id === v);
              setForm({ ...form, customer_id: v, customer_name: c?.name || "Walk-in Customer" });
            }}
              options={customers.map((c) => ({ value: c.id, label: c.name }))} testid="cs-customer"
              addOn={<button type="button" onClick={() => setShowNew(true)} data-testid="cs-customer-add"
                className="px-3 rounded-lg bg-[#1FA868] text-white"><Plus size={14} /></button>} />
          </Field>
          <Field lbl="Warehouse" required>
            <Select value={form.warehouse_id} onChange={(v) => setForm({ ...form, warehouse_id: v })}
              options={warehouses.map((w) => ({ value: w.id, label: w.name }))} testid="cs-warehouse" />
          </Field>
        </div>
        <div className="mt-4">
          <Field lbl="Sales Agent" hint="Optional — who's recording this sale">
            <TextInput value={form.sales_agent_id} onChange={(v) => setForm({ ...form, sales_agent_id: v })}
              placeholder="Agent name" testid="cs-agent" />
          </Field>
        </div>
      </Card>

      <Card className="p-6 lg:p-7 mb-5">
        <SectionHeader icon={Barcode} title="Product search" hint="Scan or type product name / SKU" />
        <div className="relative">
          <div className="flex items-center gap-2 bg-[#FAFBFB] rounded-lg px-3 py-3 border border-black/10">
            <Barcode size={16} className="text-[#1FA868]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Scan/Search Product by Code or Name" data-testid="cs-product-search"
              className="flex-1 bg-transparent focus:outline-none text-sm" />
          </div>
          {results.length > 0 && (
            <div className="absolute left-0 right-0 mt-1 bg-white rounded-xl border border-black/10 shadow-lg z-10 max-h-60 overflow-y-auto">
              {results.map((p) => (
                <button key={p.id} onClick={() => addItem(p)} data-testid={`cs-add-${p.id}`}
                  className="w-full px-4 py-2.5 text-left hover:bg-[#F0FAF3] flex items-center justify-between border-b border-black/5">
                  <div>
                    <div className="font-semibold text-sm">{p.name}</div>
                    <div className="text-[11px] text-black/55 font-mono">{p.sku} · Stock: {p.stock}</div>
                  </div>
                  <div className="font-bold tabular-nums text-sm">{fmt(p.price)}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F0FAF3] text-[#1FA868] text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left p-2.5 font-bold w-10">#</th>
                <th className="text-left p-2.5 font-bold">Product</th>
                <th className="text-left p-2.5 font-bold">Net Price</th>
                <th className="text-left p-2.5 font-bold">Stock</th>
                <th className="text-left p-2.5 font-bold">Qty</th>
                <th className="text-left p-2.5 font-bold">Discount</th>
                <th className="text-left p-2.5 font-bold">Tax</th>
                <th className="text-left p-2.5 font-bold">Subtotal</th>
                <th className="text-right p-2.5 font-bold">—</th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => (
                <tr key={i} className="border-b border-black/5">
                  <td className="p-2.5 text-black/55">{i + 1}</td>
                  <td className="p-2.5 font-semibold">{it.name}</td>
                  <td className="p-2.5"><input type="number" value={it.unit_price} onChange={(e) => upd(i, { unit_price: Number(e.target.value) })} className="w-24 px-2 py-1.5 rounded border border-black/15 text-xs tabular-nums" /></td>
                  <td className="p-2.5 text-black/55">{it.stock ?? "—"}</td>
                  <td className="p-2.5"><input type="number" value={it.qty} onChange={(e) => upd(i, { qty: Number(e.target.value) })} className="w-16 px-2 py-1.5 rounded border border-black/15 text-xs text-center" /></td>
                  <td className="p-2.5"><input type="number" value={it.discount || 0} onChange={(e) => upd(i, { discount: Number(e.target.value) })} className="w-20 px-2 py-1.5 rounded border border-black/15 text-xs" /></td>
                  <td className="p-2.5"><input type="number" value={it.tax || 0} onChange={(e) => upd(i, { tax: Number(e.target.value) })} className="w-20 px-2 py-1.5 rounded border border-black/15 text-xs" /></td>
                  <td className="p-2.5 font-bold tabular-nums">{fmt(it.qty * it.unit_price - (it.discount || 0) + (it.tax || 0))}</td>
                  <td className="p-2.5 text-right"><button onClick={() => remove(i)} className="p-1.5 rounded-md bg-rose-50 text-rose-600"><X size={12} /></button></td>
                </tr>
              ))}
              {form.items.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-black/45">No items yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-6 lg:p-7 mb-5">
        <div className="grid md:grid-cols-2 gap-5">
          <div className="space-y-3">
            <Field lbl="Order Tax"><TextInput type="number" value={form.order_tax} onChange={(v) => setForm({ ...form, order_tax: v })} suffix="%" testid="cs-ordertax" /></Field>
            <Field lbl="Order Discount"><TextInput type="number" value={form.order_discount} onChange={(v) => setForm({ ...form, order_discount: v })} suffix="₦" testid="cs-orderdiscount" /></Field>
            <Field lbl="Shipping"><TextInput type="number" value={form.shipping} onChange={(v) => setForm({ ...form, shipping: v })} suffix="₦" testid="cs-shipping" /></Field>
            <Field lbl="Payment Method">
              <Select value={form.payment_method} onChange={(v) => setForm({ ...form, payment_method: v })}
                options={[{ value: "cash", label: "Cash" }, { value: "card", label: "Card" }, { value: "transfer", label: "Bank Transfer" }, { value: "mixed", label: "Mixed" }]} testid="cs-method" />
            </Field>
            <Field lbl="Status">
              <Select value={form.status} onChange={(v) => setForm({ ...form, status: v })}
                options={[{ value: "completed", label: "Completed" }, { value: "pending", label: "Pending" }, { value: "cancelled", label: "Cancelled" }]} testid="cs-status" />
            </Field>
            <Field lbl="Paid Amount"><TextInput type="number" value={form.paid} onChange={(v) => setForm({ ...form, paid: v })} suffix="₦" testid="cs-paid" /></Field>
            <Field lbl="Note"><textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2}
              className="w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm" /></Field>
          </div>
          <div className="bg-gradient-to-br from-[#0F5132] via-[#16834D] to-[#1FA868] text-white rounded-2xl p-6 self-start">
            <div className="text-xs uppercase tracking-wider font-bold text-white/70">Summary</div>
            <div className="mt-2 space-y-2 text-sm">
              <div className="flex justify-between text-white/85"><span>Subtotal</span><span className="tabular-nums font-semibold">{fmt(subtotal)}</span></div>
              <div className="flex justify-between text-white/85"><span>Tax ({form.order_tax}%)</span><span className="tabular-nums font-semibold">{fmt(tax)}</span></div>
              <div className="flex justify-between text-white/85"><span>Discount</span><span className="tabular-nums font-semibold">-{fmt(form.order_discount)}</span></div>
              <div className="flex justify-between text-white/85"><span>Shipping</span><span className="tabular-nums font-semibold">{fmt(form.shipping)}</span></div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/20">
              <div className="text-white/70 text-xs uppercase tracking-wider font-bold">Grand Total</div>
              <div className="font-display font-extrabold text-4xl tracking-tight mt-1 tabular-nums">{fmt(total)}</div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                <div><div className="text-white/70">Paid</div><div className="font-bold">{fmt(form.paid)}</div></div>
                <div><div className="text-white/70">Due</div><div className="font-bold">{fmt(due)}</div></div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div className="bg-white rounded-2xl border border-black/[0.06] p-4 flex items-center justify-between sticky bottom-4 shadow-lg">
        <div className="text-sm text-black/55">{form.items.length} item(s) · {fmt(total)}</div>
        <div className="flex gap-2">
          <Link to={`/t/${subdomain}/sales`}><button className="px-4 py-2.5 rounded-xl border border-black/15 text-sm font-semibold">Cancel</button></Link>
          <PrimaryButton onClick={submit} disabled={busy} icon={Save} testid="cs-submit">{busy ? "Saving…" : "Submit"}</PrimaryButton>
        </div>
      </div>

      <NewCustomerMini open={showNew} onClose={() => setShowNew(false)} onCreated={(c) => { setCustomers([c, ...customers]); setForm((f) => ({ ...f, customer_id: c.id, customer_name: c.name })); }} />
    </div>
  );
}

// ═══════════════════════════════ IMPORT SALES ═══════════════════════════════
export function ImportSales() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!file) return toast.error("Choose a CSV file");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await fetch(`${API}/t/sales/import`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      const j = await r.json();
      setResult(j);
      toast.success(`Created ${j.created}, skipped ${j.skipped}`);
    } catch (e) { toast.error("Import failed"); }
    finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const csv = "date,customer,product,qty,total,paid\n2026-04-15,John Doe,Coffee Beans,2,15000,15000\n2026-04-16,Walk-in,Green Tea,1,3200,3200";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "sales_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="import-sales-page" className="p-6 lg:p-8 max-w-3xl">
      <PageHeader title="Import Sales" breadcrumb="Sales · Import Sales"
        actions={<button onClick={downloadTemplate} data-testid="imp-template"
          className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl bg-white border border-black/10 text-sm font-semibold hover:border-black/25">
          <Download size={13} /> Download Template
        </button>} />
      <Card className="p-7">
        <div className="rounded-xl border-2 border-dashed border-black/15 p-10 text-center hover:border-[#1FA868] hover:bg-[#F0FAF3] transition-colors">
          <Upload size={36} className="mx-auto text-black/30" />
          <div className="mt-3 font-semibold text-black">{file ? file.name : "Drop CSV or click to browse"}</div>
          <div className="text-[11px] text-black/45 mt-1">Columns: date, customer, product, qty, total, paid</div>
          <input type="file" accept=".csv" data-testid="imp-file" onChange={(e) => { setFile(e.target.files?.[0]); setResult(null); }}
            className="mt-4 mx-auto text-sm" />
        </div>
        <div className="mt-5">
          <PrimaryButton onClick={submit} disabled={busy || !file} icon={Upload} testid="imp-submit">
            {busy ? "Importing…" : "Start Import"}
          </PrimaryButton>
        </div>
        {result && (
          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-[#E8F5E9] p-4 text-center"><div className="text-[10px] uppercase text-[#1FA868] font-bold">Created</div><div className="font-display font-extrabold text-2xl text-[#1FA868]">{result.created}</div></div>
            <div className="rounded-xl bg-amber-50 p-4 text-center"><div className="text-[10px] uppercase text-amber-700 font-bold">Skipped</div><div className="font-display font-extrabold text-2xl text-amber-700">{result.skipped}</div></div>
          </div>
        )}
      </Card>
    </div>
  );
}

// ═══════════════════════════════ SHIPMENTS ═══════════════════════════════
export function Shipments() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [sales, setSales] = useState([]);
  const [dlg, setDlg] = useState(null);

  const load = () => api.get("/t/shipments", { params: status !== "all" ? { status } : {} }).then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/t/sales").then((r) => setSales(r.data)); }, [status]); // eslint-disable-line

  const save = async () => {
    if (!dlg.sale_id) return toast.error("Pick a sale");
    try {
      if (dlg._editing) await api.patch(`/t/shipments/${dlg._editing}`, dlg);
      else await api.post("/t/shipments", dlg);
      toast.success("Saved"); setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (s) => {
    if (!window.confirm("Delete shipment?")) return;
    await api.delete(`/t/shipments/${s.id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="shipments-page" className="p-6 lg:p-8">
      <PageHeader title="Shipments" breadcrumb="Sales · Shipments"
        actions={<PrimaryButton icon={Plus} testid="ship-new" onClick={() => setDlg({ sale_id: "", courier: "", tracking_number: "", status: "pending", note: "" })}>New Shipment</PrimaryButton>} />

      <div className="flex gap-2 mb-4 flex-wrap">
        {["all", "pending", "in_transit", "delivered", "cancelled"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`ship-status-${s}`}
            className={`px-3 py-2 rounded-lg text-xs font-semibold capitalize ${status === s ? "bg-[#1FA868] text-white" : "bg-white border border-black/10"}`}>{s.replace("_", " ")}</button>
        ))}
      </div>

      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left p-3.5 font-bold">Invoice</th>
              <th className="text-left p-3.5 font-bold">Customer</th>
              <th className="text-left p-3.5 font-bold">Courier</th>
              <th className="text-left p-3.5 font-bold">Tracking</th>
              <th className="text-left p-3.5 font-bold">Status</th>
              <th className="text-right p-3.5 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((s) => (
              <tr key={s.id} className="hover:bg-[#FAFBFB]">
                <td className="p-3.5 font-mono text-xs">{s.invoice_number}</td>
                <td className="p-3.5 font-semibold">{s.customer_name}</td>
                <td className="p-3.5">{s.courier || "—"}</td>
                <td className="p-3.5 font-mono text-xs">{s.tracking_number || "—"}</td>
                <td className="p-3.5">
                  <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-md ${
                    s.status === "delivered" ? "bg-[#E8F5E9] text-[#1FA868]" :
                    s.status === "in_transit" ? "bg-sky-50 text-sky-700" :
                    s.status === "cancelled" ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-700"
                  }`}>{s.status.replace("_", " ")}</span>
                </td>
                <td className="p-3.5 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => setDlg({ ...s, _editing: s.id })} data-testid={`ship-edit-${s.id}`}
                      className="p-2 rounded-md bg-[#E8F5E9] text-[#1FA868] hover:bg-[#D1F0DA]"><Eye size={13} /></button>
                    <button onClick={() => remove(s)} data-testid={`ship-delete-${s.id}`}
                      className="p-2 rounded-md bg-rose-50 text-rose-600 hover:bg-rose-100"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-black/45">No shipments.</td></tr>}
          </tbody>
        </table>
      </Card>

      {dlg && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDlg(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b flex items-center justify-between">
              <h3 className="font-display font-bold text-xl">{dlg._editing ? "Edit Shipment" : "New Shipment"}</h3>
              <button onClick={() => setDlg(null)} className="p-2 rounded-lg hover:bg-black/5"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              <Field lbl="Sale" required>
                <Select value={dlg.sale_id} onChange={(v) => setDlg({ ...dlg, sale_id: v })}
                  options={sales.map((s) => ({ value: s.id, label: `${s.invoice_number} — ${s.customer_name}` }))}
                  testid="ship-field-sale" />
              </Field>
              <Field lbl="Courier"><TextInput value={dlg.courier} onChange={(v) => setDlg({ ...dlg, courier: v })} testid="ship-field-courier" /></Field>
              <Field lbl="Tracking Number"><TextInput value={dlg.tracking_number} onChange={(v) => setDlg({ ...dlg, tracking_number: v })} testid="ship-field-tracking" /></Field>
              <Field lbl="Status">
                <Select value={dlg.status} onChange={(v) => setDlg({ ...dlg, status: v })}
                  options={[{ value: "pending", label: "Pending" }, { value: "in_transit", label: "In Transit" }, { value: "delivered", label: "Delivered" }, { value: "cancelled", label: "Cancelled" }]}
                  testid="ship-field-status" />
              </Field>
              <Field lbl="Note"><textarea value={dlg.note || ""} onChange={(e) => setDlg({ ...dlg, note: e.target.value })} rows={2}
                className="w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm" /></Field>
            </div>
            <div className="p-5 border-t flex justify-end gap-2">
              <button onClick={() => setDlg(null)} className="px-4 py-2 rounded-lg border border-black/15 text-sm">Cancel</button>
              <PrimaryButton onClick={save} testid="ship-save">{dlg._editing ? "Save" : "Create"}</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════ REAL-TIME SALES COUNTER ═══════════════════════════════
export function SalesCounter() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = () => api.get("/t/sales/counter/live").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  if (!data) return <div className="p-10 text-black/50">Loading…</div>;
  const maxHour = Math.max(1, ...data.hours.map((h) => h.count));

  return (
    <div data-testid="sales-counter-page" className="p-6 lg:p-8">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-bold text-[#1FA868]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#1FA868] animate-pulse" /> Live
          </div>
          <h1 className="font-display font-extrabold text-3xl text-black tracking-tight mt-1">Real-time Sales Counter</h1>
          <div className="text-xs text-black/55 mt-1">Sales · Real-time Counter · Updates every 5s</div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-5">
        <div className="bg-gradient-to-br from-[#0F5132] to-[#1FA868] text-white rounded-2xl p-6">
          <div className="text-xs uppercase font-bold text-white/70">Today's Revenue</div>
          <div className="font-display font-extrabold text-4xl mt-2 tabular-nums">{fmt(data.today.revenue)}</div>
          <div className="text-sm text-white/70 mt-1">{data.today.count} orders · {data.today.items_sold} items</div>
        </div>
        <div className="bg-white rounded-2xl border border-black/5 p-6">
          <div className="text-xs uppercase font-bold text-black/55">This Hour</div>
          <div className="font-display font-extrabold text-4xl mt-2 tabular-nums text-[#1FA868]">{data.this_hour.count}</div>
          <div className="text-sm text-black/55 mt-1">orders · {fmt(data.this_hour.revenue)}</div>
        </div>
        <div className="bg-white rounded-2xl border border-black/5 p-6">
          <div className="text-xs uppercase font-bold text-black/55">Recent Sales</div>
          <div className="mt-2 space-y-1">
            {data.recent.slice(0, 4).map((s) => (
              <div key={s.id} className="flex items-center justify-between text-xs">
                <span className="font-mono">{s.invoice_number}</span>
                <span className="font-bold tabular-nums text-[#1FA868]">{fmt(s.total)}</span>
              </div>
            ))}
            {data.recent.length === 0 && <div className="text-xs text-black/45">None yet today.</div>}
          </div>
        </div>
      </div>

      <Card className="p-6">
        <h3 className="font-display font-bold text-lg text-black mb-5">Hourly Distribution (today)</h3>
        <div className="flex items-end gap-1 h-40">
          {data.hours.map((h) => {
            const hgt = (h.count / maxHour) * 100;
            return (
              <div key={h.h} className="flex-1 flex flex-col items-center gap-1">
                <div className="text-[9px] text-black/45 font-bold">{h.count || ""}</div>
                <div className="w-full bg-[#F0FAF3] rounded-t-md overflow-hidden flex items-end" style={{ height: 120 }}>
                  <div className="w-full rounded-t-md bg-gradient-to-t from-[#1FA868] to-[#16834D]" style={{ height: `${Math.max(2, hgt)}%` }} />
                </div>
                <div className="text-[9px] text-black/45">{h.h}</div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// ═══════════════════════════════ CUSTOMER SCREEN ═══════════════════════════════
export function CustomerScreen() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/t/pos/customer-screen").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, []);
  if (!data) return <div className="p-10 text-black/50">Loading…</div>;
  const s = data.last_sale;

  return (
    <div data-testid="customer-screen" className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-[#0F5132] via-[#16834D] to-[#1FA868] text-white p-10 flex items-center justify-center">
      <div className="max-w-3xl w-full text-center">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-white/60">{data.tenant.name}</div>
        <h1 className="font-display font-extrabold text-5xl lg:text-6xl tracking-tight mt-2">{data.hero || "Thank you for shopping"}</h1>
        {s ? (
          <div className="mt-10 bg-white/10 backdrop-blur rounded-3xl p-8 text-left">
            <div className="flex items-center justify-between text-white/70 text-xs uppercase tracking-wider font-bold">
              <span>Order</span>
              <span className="font-mono">{s.invoice_number}</span>
            </div>
            <div className="mt-4 divide-y divide-white/15">
              {(s.items || []).map((i, idx) => (
                <div key={idx} className="py-3 flex justify-between">
                  <span>{i.name} <span className="text-white/60">× {i.qty}</span></span>
                  <span className="font-bold tabular-nums">{fmt((i.line_total ?? i.qty * i.unit_price))}</span>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-5 border-t border-white/20 flex items-center justify-between">
              <span className="text-white/70 uppercase tracking-wider text-xs font-bold">Total</span>
              <span className="font-display font-extrabold text-5xl tracking-tight tabular-nums">{fmt(s.total)}</span>
            </div>
            {s.paid >= s.total && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 text-xs font-bold uppercase tracking-wider">
                ✓ Paid in full — Thank you!
              </div>
            )}
          </div>
        ) : (
          <div className="mt-8 text-white/70">Waiting for next order…</div>
        )}
      </div>
    </div>
  );
}
